/**
 * product-listing.js  (entry file, loaded on collection and search pages)
 * ------------------------------------------------------------------
 * Makes filters, sorting and the mobile filter UI work without page reloads,
 * using ONLY Shopify native filtering (Search & Discovery app filters).
 *
 * The flow for every shopper action is always the same:
 *
 *   1. Read the current selection from the URL           (product-listing-state.js)
 *   2. Work out the new selection (add/remove a filter...)(product-listing-state.js)
 *   3. Ask Shopify for the section HTML of that new URL   (product-listing-renderer.js)
 *   4. Swap the filters / grid / pagination on the page   (product-listing-renderer.js)
 *   5. Put the new URL in the address bar (shareable, back button works)
 *
 * Desktop: every checkbox click applies immediately.
 * Mobile:
 *   - Quick filter pills (Color, Crafts...) apply immediately.
 *   - The full "Refine by" drawer collects changes and applies them on "Apply".
 *
 * Pagination links are plain links (normal page load), as Shopify renders them.
 */

import {
  areParamsEqual,
  buildListingUrl,
  clearAllFilters,
  isFilterValueSelected,
  isPriceRangeSelected,
  parseFilterValues,
  parsePriceRange,
  setFilterValues,
  setPriceRange,
  setSort,
} from 'product-listing-state';
import { fetchListingDocument, replaceListingRegions } from 'product-listing-renderer';
import { LISTING_SELECTORS, getPillPanelSelector } from 'product-listing-selectors';
import { applySavedGridColumns, initGridColumns } from 'product-grid-columns';
import { initListingCounter } from 'product-listing-counter';
import { initStickyOffset } from 'product-listing-sticky-offset';
import { initScrollRestore } from 'product-listing-scroll-restore';
import { handleDrawerChipClick, renderDrawerSelectedFilters } from 'product-drawer-selected-filters';
import { initPriceRangeAvailability, updatePriceRangeAvailability } from 'product-price-range-availability';
import { initPriceFilterCurrency } from 'product-price-filter-currency';

/* ------------------------------------------------------------------ */
/* Small DOM helpers                                                   */
/* ------------------------------------------------------------------ */

/** @returns {URLSearchParams} Selection currently shown on the page */
function getCurrentParams() {
  return new URLSearchParams(document.location.search);
}

/**
 * Ticks / unticks every filter checkbox so it matches the given selection.
 * Needed because the same option can exist twice (desktop sidebar + mobile pill),
 * and because only one price range can be active.
 * @param {HTMLElement} listingRoot
 * @param {URLSearchParams} params
 */
function syncCheckboxesWithParams(listingRoot, params) {
  listingRoot.querySelectorAll(LISTING_SELECTORS.filterOption).forEach((checkbox) => {
    const filterValues = parseFilterValues(checkbox.dataset.filterValues);
    checkbox.checked = isFilterValueSelected(params, checkbox.dataset.filterParam, filterValues);
  });

  listingRoot.querySelectorAll(LISTING_SELECTORS.priceOption).forEach((checkbox) => {
    const priceRange = parsePriceRange(checkbox.dataset.priceMin, checkbox.dataset.priceMax);
    checkbox.checked = isPriceRangeSelected(params, priceRange);
  });
}

/**
 * Works out the new selection after a filter checkbox was ticked / unticked.
 * @param {HTMLInputElement} checkbox
 * @param {URLSearchParams} params Selection before the click
 * @returns {URLSearchParams|null} New selection, or null if the element is not a filter checkbox
 */
function getParamsAfterCheckboxChange(checkbox, params) {
  if (checkbox.matches(LISTING_SELECTORS.filterOption)) {
    const filterValues = parseFilterValues(checkbox.dataset.filterValues);
    return setFilterValues(params, checkbox.dataset.filterParam, filterValues, checkbox.checked);
  }

  if (checkbox.matches(LISTING_SELECTORS.priceOption)) {
    const priceRange = parsePriceRange(checkbox.dataset.priceMin, checkbox.dataset.priceMax);
    return setPriceRange(params, checkbox.checked ? priceRange : null);
  }

  return null;
}

/**
 * Opens or closes one filter group ("Size +"). CSS animates the panel height.
 * @param {HTMLElement} filterGroup [data-filter-group] element
 * @param {boolean} isOpen
 */
function setFilterGroupOpen(filterGroup, isOpen) {
  filterGroup.classList.toggle('is-open', isOpen);
  const groupToggle = filterGroup.querySelector(LISTING_SELECTORS.filterGroupToggle);
  if (groupToggle) groupToggle.setAttribute('aria-expanded', String(isOpen));
}

/**
 * Closes every filter group inside the container except the one being opened.
 * @param {HTMLElement} container Element holding the filter groups (the filter drawer)
 * @param {HTMLElement} keptFilterGroup [data-filter-group] element that stays as it is
 */
function closeOtherFilterGroups(container, keptFilterGroup) {
  container.querySelectorAll(LISTING_SELECTORS.filterGroup).forEach((filterGroup) => {
    if (filterGroup !== keptFilterGroup && filterGroup.classList.contains('is-open')) {
      setFilterGroupOpen(filterGroup, false);
    }
  });
}

/**
 * @param {HTMLElement} listingRoot
 * @returns {Set<string>} Keys of the filter groups the shopper has expanded
 */
function getOpenFilterGroupKeys(listingRoot) {
  const filterGroups = [...listingRoot.querySelectorAll(LISTING_SELECTORS.filterGroup)];
  return new Set(
    filterGroups
      .filter((filterGroup) => filterGroup.classList.contains('is-open'))
      .map((filterGroup) => filterGroup.dataset.filterGroup)
  );
}

/**
 * Re-opens the filter groups that were open before the sidebar HTML was replaced.
 * @param {HTMLElement} listingRoot
 * @param {Set<string>} openGroupKeys
 */
function restoreOpenFilterGroups(listingRoot, openGroupKeys) {
  listingRoot.querySelectorAll(LISTING_SELECTORS.filterGroup).forEach((filterGroup) => {
    if (openGroupKeys.has(filterGroup.dataset.filterGroup)) {
      setFilterGroupOpen(filterGroup, true);
    }
  });
}

/** Must match the panel transition duration in product-listing.css (.listing-pill-panel). */
const PANEL_TRANSITION_MS = 300;

/**
 * Resolves when the element's CSS transition has finished, so new HTML is not swapped in
 * while a panel is still sliding closed. The timer is only a safety net in case
 * "transitionend" never fires (e.g. reduced motion / element removed).
 * @param {HTMLElement|null} element
 * @returns {Promise<void>}
 */
function waitForTransitionEnd(element) {
  if (!element) return Promise.resolve();

  return new Promise((resolve) => {
    const finish = () => {
      element.removeEventListener('transitionend', finish);
      resolve();
    };
    element.addEventListener('transitionend', finish);
    window.setTimeout(finish, PANEL_TRANSITION_MS + 50);
  });
}

/**
 * Shows / hides a dropdown panel (mobile pill panel or desktop sort menu). CSS animates it.
 * @param {HTMLElement} panel
 * @param {boolean} isOpen
 */
function setPanelOpen(panel, isOpen) {
  panel.classList.toggle('is-open', isOpen);
  panel.setAttribute('aria-hidden', String(!isOpen));
}

/**
 * Lets other scripts (wishlist, reviews...) know that new product cards are on the page.
 * @param {string} listingUrl
 */
function announceListingUpdated(listingUrl) {
  document.dispatchEvent(new CustomEvent('product-listing:updated', { detail: { url: listingUrl } }));
  // Flits wishlist listens to this event to attach its buttons to new product cards.
  document.dispatchEvent(new CustomEvent('Flits:AjaxCollection:CollectionUpdated', { bubbles: true }));
}

/**
 * If the shopper has scrolled past the top of the results, bring the first products into view.
 * @param {HTMLElement} listingRoot
 */
function scrollToResultsIfNeeded(listingRoot) {
  const listingTop = listingRoot.getBoundingClientRect().top;
  if (listingTop < 0) {
    listingRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/* ------------------------------------------------------------------ */
/* One product listing on the page                                     */
/* ------------------------------------------------------------------ */

/**
 * @param {HTMLElement} listingRoot The [data-product-listing] element
 */
function initProductListing(listingRoot) {
  const sectionId = listingRoot.dataset.sectionId;
  if (!sectionId) {
    console.error('[product-listing] data-section-id is missing, filters will reload the page instead.');
  }

  /** Number of the latest results request; older answers are ignored. */
  let latestRequestNumber = 0;

  /** Selection made inside the open mobile drawer, applied only on "Apply". null = drawer closed. */
  let pendingDrawerParams = null;

  /** Query string of the results currently on screen (used to ignore "#hash" only history changes). */
  let shownQueryString = document.location.search;

  /* ---------------- Showing results ---------------- */

  /**
   * Loads and shows the results for a selection.
   * @param {URLSearchParams} params
   * @param {{isHistoryUpdate?: boolean, waitBeforeSwap?: Promise<void>}} options
   *   isHistoryUpdate=false when called from the back button;
   *   waitBeforeSwap = a closing-panel animation to finish before the new HTML is shown
   */
  async function showResults(params, { isHistoryUpdate = true, waitBeforeSwap = Promise.resolve() } = {}) {
    const listingUrl = buildListingUrl(document.location.pathname, params);

    if (!sectionId) {
      document.location.assign(listingUrl);
      return;
    }

    latestRequestNumber += 1;
    const requestNumber = latestRequestNumber;
    listingRoot.classList.add('is-loading');
    listingRoot.setAttribute('aria-busy', 'true');

    try {
      const openGroupKeys = getOpenFilterGroupKeys(listingRoot);
      // Load the results while a closing panel finishes its animation, then swap.
      const [newDocument] = await Promise.all([fetchListingDocument(listingUrl, sectionId), waitBeforeSwap]);

      // The shopper clicked something else while we were waiting: that newer request wins.
      if (requestNumber !== latestRequestNumber) return;

      replaceListingRegions(listingRoot, newDocument);
      restoreOpenFilterGroups(listingRoot, openGroupKeys);
      applySavedGridColumns(listingRoot);

      if (isHistoryUpdate) {
        window.history.pushState({ isProductListing: true }, '', listingUrl);
      }
      shownQueryString = document.location.search;

      announceListingUpdated(listingUrl);
      scrollToResultsIfNeeded(listingRoot);
    } catch (error) {
      // Never leave the shopper stuck: fall back to a normal page load with the same filters.
      console.error('[product-listing] Could not update the results without reloading, loading the page instead.', error);
      document.location.assign(listingUrl);
    } finally {
      if (requestNumber === latestRequestNumber) {
        listingRoot.classList.remove('is-loading');
        listingRoot.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------------- Mobile "Refine by" drawer ---------------- */

  function getFilterDrawer() {
    return listingRoot.querySelector(LISTING_SELECTORS.filterDrawer);
  }

  function isDrawerOpen() {
    const filterDrawer = getFilterDrawer();
    return Boolean(filterDrawer && filterDrawer.classList.contains('is-open'));
  }

  function openDrawer() {
    const filterDrawer = getFilterDrawer();
    if (!filterDrawer) return;

    closeAllPanels();
    pendingDrawerParams = getCurrentParams();
    renderDrawerSelectedFilters(filterDrawer);
    filterDrawer.classList.add('is-open');
    document.documentElement.classList.add('is-scroll-locked');
  }

  /**
   * @param {{isApplying: boolean}} options isApplying=true when "Apply" was pressed
   */
  function closeDrawer({ isApplying }) {
    const filterDrawer = getFilterDrawer();
    if (!filterDrawer) return;

    const paramsToApply = pendingDrawerParams;
    pendingDrawerParams = null;
    filterDrawer.classList.remove('is-open');
    document.documentElement.classList.remove('is-scroll-locked');

    if (isApplying && paramsToApply && !areParamsEqual(paramsToApply, getCurrentParams())) {
      showResults(paramsToApply);
    } else {
      // Closed without applying: put the checkboxes back to what is really applied.
      syncCheckboxesWithParams(listingRoot, getCurrentParams());
    }
  }

  function clearDrawerSelection() {
    pendingDrawerParams = clearAllFilters(pendingDrawerParams || getCurrentParams());
    syncCheckboxesWithParams(listingRoot, pendingDrawerParams);
    closeDrawer({ isApplying: true });
  }

  /* ---------------- Mobile pills + desktop sort dropdown ---------------- */

  function closeAllPanels() {
    listingRoot.querySelectorAll(LISTING_SELECTORS.pillPanel).forEach((pillPanel) => {
      setPanelOpen(pillPanel, false);
    });
    listingRoot.querySelectorAll(LISTING_SELECTORS.pillToggle).forEach((pillToggle) => {
      pillToggle.setAttribute('aria-expanded', 'false');
    });

    const sortMenu = listingRoot.querySelector(LISTING_SELECTORS.sortDropdownMenu);
    const sortToggle = listingRoot.querySelector(LISTING_SELECTORS.sortDropdownToggle);
    if (sortMenu) setPanelOpen(sortMenu, false);
    if (sortToggle) sortToggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Closes all panels and returns a promise that resolves when the one that was open
   * has finished sliding closed (resolves immediately when nothing was open).
   * @returns {Promise<void>}
   */
  function closeAllPanelsAnimated() {
    const openPanel = listingRoot.querySelector(
      `${LISTING_SELECTORS.pillPanel}.is-open, ${LISTING_SELECTORS.sortDropdownMenu}.is-open`
    );
    closeAllPanels();
    return waitForTransitionEnd(openPanel);
  }

  /** @param {HTMLElement} pillToggle */
  function togglePillPanel(pillToggle) {
    const pillPanel = listingRoot.querySelector(getPillPanelSelector(pillToggle.dataset.pillToggle));
    if (!pillPanel) {
      console.warn(`[product-listing] No panel found for pill "${pillToggle.dataset.pillToggle}".`);
      return;
    }

    const isOpening = !pillPanel.classList.contains('is-open');
    closeAllPanels();
    setPanelOpen(pillPanel, isOpening);
    pillToggle.setAttribute('aria-expanded', String(isOpening));
  }

  function toggleSortDropdown() {
    const sortMenu = listingRoot.querySelector(LISTING_SELECTORS.sortDropdownMenu);
    const sortToggle = listingRoot.querySelector(LISTING_SELECTORS.sortDropdownToggle);
    if (!sortMenu || !sortToggle) return;

    const isOpening = !sortMenu.classList.contains('is-open');
    closeAllPanels();
    setPanelOpen(sortMenu, isOpening);
    sortToggle.setAttribute('aria-expanded', String(isOpening));
  }

  /* ---------------- Event handlers ---------------- */

  /** A filter / price checkbox was ticked or unticked. */
  function handleCheckboxChange(event) {
    const checkbox = event.target;
    const isInsideOpenDrawer = isDrawerOpen() && getFilterDrawer().contains(checkbox);
    const paramsBefore = isInsideOpenDrawer ? pendingDrawerParams : getCurrentParams();

    const paramsAfter = getParamsAfterCheckboxChange(checkbox, paramsBefore);
    if (!paramsAfter) return;

    syncCheckboxesWithParams(listingRoot, paramsAfter);

    if (isInsideOpenDrawer) {
      pendingDrawerParams = paramsAfter; // applied later with the "Apply" button
      renderDrawerSelectedFilters(getFilterDrawer());
      return;
    }

    showResults(paramsAfter, { waitBeforeSwap: closeAllPanelsAnimated() });
  }

  /** Every button click inside the listing goes through here. */
  function handleClick(event) {
    const clickedElement = event.target;

    const filterGroupToggle = clickedElement.closest(LISTING_SELECTORS.filterGroupToggle);
    if (filterGroupToggle) {
      const filterGroup = filterGroupToggle.closest(LISTING_SELECTORS.filterGroup);
      if (!filterGroup) return;

      const isOpening = !filterGroup.classList.contains('is-open');
      // Only one filter group open at a time, in the desktop sidebar and the mobile drawer alike
      // (both are the same [data-filter-drawer] element)
      const filterGroupContainer = filterGroup.closest(LISTING_SELECTORS.filterDrawer);
      if (isOpening && filterGroupContainer) {
        closeOtherFilterGroups(filterGroupContainer, filterGroup);
      }
      setFilterGroupOpen(filterGroup, isOpening);
      return;
    }

    if (isDrawerOpen() && handleDrawerChipClick(getFilterDrawer(), event)) {
      return;
    }

    const removeFilterButton = clickedElement.closest(LISTING_SELECTORS.removeFilterButton);
    if (removeFilterButton) {
      const filterValues = parseFilterValues(removeFilterButton.dataset.filterValues);
      showResults(setFilterValues(getCurrentParams(), removeFilterButton.dataset.filterParam, filterValues, false));
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.removePriceButton)) {
      showResults(setPriceRange(getCurrentParams(), null));
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.clearFiltersButton)) {
      showResults(clearAllFilters(getCurrentParams()));
      return;
    }

    const sortOption = clickedElement.closest(LISTING_SELECTORS.sortOption);
    if (sortOption) {
      showResults(setSort(getCurrentParams(), sortOption.dataset.sortOption), {
        waitBeforeSwap: closeAllPanelsAnimated(),
      });
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.drawerOpenButton)) {
      openDrawer();
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.drawerCloseButton)) {
      closeDrawer({ isApplying: false });
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.drawerApplyButton)) {
      closeDrawer({ isApplying: true });
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.drawerClearButton)) {
      clearDrawerSelection();
      return;
    }

    const pillToggle = clickedElement.closest(LISTING_SELECTORS.pillToggle);
    if (pillToggle) {
      togglePillPanel(pillToggle);
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.pillCloseButton)) {
      closeAllPanels();
      return;
    }

    if (clickedElement.closest(LISTING_SELECTORS.sortDropdownToggle)) {
      toggleSortDropdown();
    }
  }

  /** Clicking anywhere outside an open pill panel / sort dropdown closes it. */
  function handleOutsideClick(event) {
    const isInsidePanel =
      event.target.closest(LISTING_SELECTORS.pillPanel) ||
      event.target.closest(LISTING_SELECTORS.pillToggle) ||
      event.target.closest(LISTING_SELECTORS.sortDropdown);

    if (!isInsidePanel) {
      closeAllPanels();
    }
  }

  function handleEscapeKey(event) {
    if (event.key !== 'Escape') return;
    closeAllPanels();
    if (isDrawerOpen()) {
      closeDrawer({ isApplying: false });
    }
  }

  /** Browser back / forward buttons: show the results of that URL. */
  function handleHistoryChange() {
    if (document.location.search === shownQueryString) return; // only the #hash changed

    if (isDrawerOpen()) {
      closeDrawer({ isApplying: false });
    }
    showResults(getCurrentParams(), { isHistoryUpdate: false });
  }

  listingRoot.addEventListener('change', handleCheckboxChange);
  listingRoot.addEventListener('click', handleClick);
  document.addEventListener('click', handleOutsideClick);
  document.addEventListener('keydown', handleEscapeKey);
  window.addEventListener('popstate', handleHistoryChange);

  initGridColumns(listingRoot);
  initListingCounter(listingRoot);
  initStickyOffset(listingRoot);
  initScrollRestore(listingRoot);
  initPriceRangeAvailability(listingRoot);
  initPriceFilterCurrency(listingRoot, {
    onPriceRowsChanged: () => updatePriceRangeAvailability(listingRoot),
  });
}

/* ------------------------------------------------------------------ */
/* Start                                                               */
/* ------------------------------------------------------------------ */

document.querySelectorAll(LISTING_SELECTORS.listingRoot).forEach((listingRoot) => {
  try {
    initProductListing(listingRoot);
  } catch (error) {
    // Filters then still work as normal links / page loads where possible.
    console.error('[product-listing] Could not start the product listing.', error);
  }
});
